import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture, coverScale } from '../artwork-texture.js';
import { vdjColor } from './lib/vdjLook.js';

// Pinscreen - the pin-art toy: a framed board of chrome pins pushed from
// behind into the relief of the cover, with waves running across it in time.
// Pop, R&B, vocal house, hip-hop. Rules of kiosk-app/CLAUDE.md (R1-R11);
// danceability 0.65 = R9's middle band: cuts on the phrase, moderate pulses.
// (Corrected 2026-10-14 - the first version, kept as nothing, had 9 216 pins
// of 7 px that read as a crawling texture, grey heads, a flat matcap.)
//
// FEWER, BIGGER PINS. 80 x 45 at tier 2 (64 x 36, 48 x 27): a head is 14-20
// px on a 1080p screen and reads as a chrome ball; its diameter is 85 % of
// the pitch, so a thin black gap separates the balls.
//
// ONE InstancedMesh. A pin = a hemispherical head + a stem, built once with
// EXACT normals (sphere: the radius vector, cylinder: the radial vector). A
// pin only translates along z, so the normals stay exact (R5). The stem's
// back ring is pinned to the base plane in the vertex shader: nothing ever
// sticks out behind the board. Per instance only its place (iGrid) and, when
// a vertex shader cannot read textures, the cover under it (iCover, static,
// rewritten once per track). The height of every pin comes from uniforms:
//   rel   sqrt(luminance) of the cover under it (vertex texture fetch,
//         clamped inside the cover's UVs: the top row stays level)
//   front a reform front (radial after a phrase, top-down after a drop):
//         passed pins show rel, the others `outside` (0 flat, PUSH pushed)
//   h     RELIEF_H x mix(outside, rel x fade, front), the drop's push,
//         + 4 radial waves, x the bar's recoil
//
// THE LOOK (R4, R5). The cover TINTS the chrome, like anodised metal:
//   head = matcap body x coverColour x 1.4 + the matcap's two white boxes
// with the cover's colour pushed 20 % in saturation. The matcap is this
// file's own (makeMatcap): a grazing light from the upper left, 40 % of the
// ball in near-black, two wide soft-edged boxes above the bloom threshold.
// Pins are darkened with their recess (x 0.55 fully back): the occlusion
// between pins, so the relief reads by the light - forward pins catch it.
// MSAA: the board renders into a private 4x multisampled target
// (no-antialiasing-private-msaa-target), shown by a full-frame quad.
//
// THE FRAME. A matte black rim two pitches wide on the base plane, with a
// one-pixel lit edge (R1: by its distance in pixels) - the toy's frame.
//
// FRAMING (R6) is computed ONCE per aspect, over the whole yaw range: the
// pins span 85 % of the width at yaw 0, centred, with the 5 % margin kept at
// every yaw. The camera then only turns (front view, 10 deg down, +-12 deg):
// solving the fit per frame made the board jump (the solver's discrete
// choices changed from frame to frame).
// Motion runs on a SMOOTH clock (seconds / beat length, never re-anchored):
// the grid's phase re-anchors (every 3 s in the harness) made the waves and
// the recoil hop.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pinscreen',
  family: 'objects',
  danceability: 0.65,
  requiresTier: 0,
  energyLevel: 0.58,
  bpmMin: 85,
  bpmMax: 125,
  movement: 0.55,
  tonality: 0.75,
  enabled: true
};

// seg / rings: the head's segments around and rings; stemSeg: stem faces
const TIER = {
  0: { nx: 48, ny: 27, seg: 16, rings: 5, stemSeg: 6 },
  1: { nx: 64, ny: 36, seg: 14, rings: 4, stemSeg: 6 },
  2: { nx: 80, ny: 45, seg: 12, rings: 4, stemSeg: 6 }
};
const DEFAULT_TIER = 2;

// --- the board, world units ------------------------------------------------------
const BOARD_W = 16, BOARD_H = 9;
const HEAD_R = 0.425;             // x pitch: a head is 85 % of the pitch across
const STEM_R = 0.12;              // x pitch
const BASE_Z = -0.05;             // the base plane: stems end here
const FRAME_PITCHES = 2;
const RELIEF_H = 1.6;             // the cover's relief at full luminance
const PUSH = 1.25;                // the drop: x RELIEF_H
const WAVE_AMP = 0.32;
const WAVE_W = 0.9;
const WAVE_SPEED_BEATS = 2.2;
const WAVE_LIFE_BEATS = 3.0;
const BAR_RECOIL = 0.10;
const RECESS_DARK = 0.55;         // brightness of a pin fully back
const H_FIT = RELIEF_H * PUSH + WAVE_AMP * 1.5 + 0.1;

// --- the view ----------------------------------------------------------------------
const FOV = 18;                     // long lens: at 30 deg the raised pins spread 8 % wider than the board; 14 gained nothing more (the pins hit the height first)
const PITCH = 8 * Math.PI / 180;      // a front view, slightly from above (10 max)
const YAW_MAX = 12 * Math.PI / 180;
const YAW_PHRASE = 7 * Math.PI / 180; // the phrase's turn: to +-7 deg
const YAW_SWAY = 4 * Math.PI / 180;   // a slow sway on top
const SWAY_BEATS = 64;
const TURN_BEATS = 8;
const WIDTH_FRAC = 0.85;              // the pins span this much of the width
const MARGIN = 0.05;                  // of the frame height
const PREFER_DOWN = 0.05;             // of the height: lower if there is room

// --- the look --------------------------------------------------------------------------
const MATCAP_SIZE = 256;
const MATCAP_GAIN = 1.4;              // encoding headroom (sqrt(v / gain) in bytes)
const BOX_LUM = 1.15;                 // the two boxes: over the bloom threshold
const COVER_GAIN = 1.4;
const COVER_SAT = 1.2;
const RIM_GAIN = 0.05;              // 0.18 drew a grey ring round every head
const SAFE_FLOOR = 0.55;
const FREE_BPM = 105;

const _size = new THREE.Vector2();
const _v = new THREE.Vector3();
const _m = new THREE.Matrix4();

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function smoothstep(e0, e1, x) { return smooth01((x - e0) / (e1 - e0)); }

// ---------------------------------------------------------------------------
// The matcap (R5): a ball lit by a grazing light from the upper left. R =
// the body (sqrt-encoded over MATCAP_GAIN), G = the boxes (0..1). 40 % of the
// disc is under 0.06; two wide boxes with soft edges in the upper left.
// ---------------------------------------------------------------------------
const BOXES = [
  { cx: -0.36, cy: 0.44, hw: 0.30, hh: 0.15, rr: 0.10, soft: 0.10 },
  { cx: -0.66, cy: 0.02, hw: 0.08, hh: 0.24, rr: 0.07, soft: 0.07 }
];
function sdRoundBox(x, y, b) {
  const qx = Math.abs(x - b.cx) - (b.hw - b.rr), qy = Math.abs(y - b.cy) - (b.hh - b.rr);
  return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0) - b.rr;
}
export function makeMatcap(n) {
  const size = n || MATCAP_SIZE;
  const data = new Uint8Array(size * size * 4);
  const L = new THREE.Vector3(-0.62, 0.62, 0.48).normalize();
  let dark = 0, disc = 0;
  for (let j = 0; j < size; j++) {
    const y = ((j + 0.5) / size) * 2 - 1;
    for (let i = 0; i < size; i++) {
      const x = ((i + 0.5) / size) * 2 - 1, o = (j * size + i) * 4;
      const r2 = Math.min(1, x * x + y * y), nz = Math.sqrt(1 - r2);
      const ndl = x * L.x + y * L.y + nz * L.z;
      // the body: a steep falloff from the lit upper left into near black
      let body = 0.012 + 0.70 * Math.pow(smoothstep(0.08, 0.95, ndl), 1.5);
      // a faint cool reflection of the floor at the very bottom (chrome, not paint)
      body += 0.05 * smoothstep(-0.6, -0.95, y) * nz;
      let box = 0;
      for (const b of BOXES) box = Math.max(box, smoothstep(b.soft, -b.soft * 0.3, sdRoundBox(x, y, b)));
      if (x * x + y * y <= 1) { disc++; if (body < 0.06 && box < 0.05) dark++; }
      data[o] = Math.round(Math.sqrt(clamp01(body / MATCAP_GAIN)) * 255);
      data[o + 1] = Math.round(box * 255);
      data[o + 2] = 0; data[o + 3] = 255;
    }
  }
  return { data, size, darkShare: dark / disc };
}

// ---------------------------------------------------------------------------
// Shaders (GLSL ES 1.0 constructs; three adds the version line)
// ---------------------------------------------------------------------------

function vertexShader(vtf) {
  return `
  precision highp float;   // highp in the vertex stage: grid coordinates and heights (fp16 on the TV)
  ${vtf ? '#define PIN_VTF 1' : ''}
  // WebGL1 spelling; three compiles GLSL 3, where it is textureLod
  #if __VERSION__ >= 300
  #define texture2DLod textureLod
  #endif
  attribute vec2 iGrid;          // the pin's place, 0..1 on the board
  attribute vec4 iCover;         // fallback: the cover under it
  attribute float aPart;         // 1 head, 0 stem front ring, -1 stem back ring
  uniform mat4 uProj;
  uniform mat4 uView;
  uniform sampler2D uCover;
  uniform vec2 uCoverScale;
  uniform vec2 uCoverClamp;      // half a texel of the sampled mip: stay inside the cover
  uniform float uHasCover;
  uniform float uLod;
  uniform vec2 uBoard;
  uniform float uRelief;
  uniform float uFade;
  uniform float uFront;
  uniform float uFrontW;
  uniform float uMode;
  uniform float uOutside;
  uniform float uPush;
  uniform float uRecoil;
  uniform vec4 uWaves[4];        // x, y (world), radius, amplitude
  uniform float uTime;
  varying vec3 vN;
  varying vec3 vCol;
  varying float vPart;
  varying float vCover;
  varying float vLight;          // the recess occlusion
  varying vec4 vClip;

  float lumOf(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

  void main() {
    vec2 uvc = clamp((iGrid - 0.5) * uCoverScale + 0.5, uCoverClamp, 1.0 - uCoverClamp);
    vec3 col;
    float rel;
  #ifdef PIN_VTF
    col = texture2DLod(uCover, uvc, uLod).rgb;
  #else
    col = iCover.rgb;
  #endif
    if (uHasCover > 0.5) {
      rel = sqrt(clamp(lumOf(col), 0.0, 1.0));
    } else {
      vec2 q = (iGrid - 0.5) * vec2(uBoard.x / uBoard.y, 1.0);
      rel = 0.5 + 0.5 * cos(length(q) * 14.0 - uTime * 0.6);
      col = vec3(0.55);
    }
    vec2 xy = (iGrid - 0.5) * uBoard;
    float m = uMode < 0.5 ? length((iGrid - 0.5) * vec2(uBoard.x / uBoard.y, 1.0)) / 0.95 : 1.0 - iGrid.y;
    float front = smoothstep(uFront, uFront - uFrontW, m);
    float hr = mix(uOutside, rel * uRelief * uFade, front);
    hr = mix(hr, ${PUSH.toFixed(2)}, uPush);
    vCover = mix(clamp(uOutside, 0.0, 1.0), uFade, front);
    float h = hr * ${RELIEF_H.toFixed(2)};
    for (int i = 0; i < 4; i++) {
      vec4 w = uWaves[i];
      float d = length(xy - w.xy) - w.z;
      h += w.w * exp(-d * d / ${(WAVE_W * WAVE_W).toFixed(3)});
    }
    h *= uRecoil;
    // occlusion: a pin fully back gets x RECESS_DARK, a pin at full relief 1
    vLight = mix(${RECESS_DARK.toFixed(2)}, 1.0, clamp(h / ${RELIEF_H.toFixed(2)}, 0.0, 1.0));
    vec3 p = position + vec3(xy, h);
    if (aPart < -0.5) p.z = ${BASE_Z.toFixed(2)};      // the stem's back ring stays on the base plane
    vN = normalize(mat3(uView) * normal);
    vCol = col;
    vPart = aPart;
    vClip = uProj * uView * vec4(p, 1.0);
    gl_Position = vClip;
  }
`;
}

const fragmentShader = `
  precision mediump float;
  uniform sampler2D uMat;
  uniform float uNormals;
  uniform float uGain;
  uniform vec3 uSilver;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec3 vN;
  varying vec3 vCol;
  varying float vPart;
  varying float vCover;
  varying float vLight;
  varying vec4 vClip;

  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    vec3 n = normalize(vN);
    if (uNormals > 0.5) { gl_FragColor = vec4(n * 0.5 + 0.5, 1.0); return; }
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.02));
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = m.r * m.r * ${MATCAP_GAIN.toFixed(2)};
    float box = m.g * ${BOX_LUM.toFixed(2)};
    float rim = pow(1.0 - n.z, 4.0) * ${RIM_GAIN.toFixed(2)};
    // the cover's colour, 20 % more saturated, tinting the chrome (anodised)
    float l = dot(vCol, vec3(0.2126, 0.7152, 0.0722));
    vec3 tint = max(mix(vec3(l), vCol, ${COVER_SAT.toFixed(2)}), 0.0);
    tint = mix(uSilver, tint * ${COVER_GAIN.toFixed(2)}, vCover);
    vec3 col;
    if (vPart > 0.5) {
      // the tinted body stays under the bloom threshold (a white cover x 1.4
      // reached 1.0): only the two boxes bloom (R1, R5)
      vec3 b = tint * (body + rim);
      b *= min(1.0, 0.70 / max(dot(b, vec3(0.2126, 0.7152, 0.0722)), 1e-4));
      col = b + vec3(box);
    } else {
      // the stem: dark tinted chrome, never flat - kept low, or the shafts of
      // the raised pins read as bright diagonal scratches across the board
      col = tint * (body + rim) * 0.16;
    }
    col *= vLight;
    gl_FragColor = vec4(col * uGain * safeAtt(vClip.xy / vClip.w), 1.0);
  }
`;

// The frame: matte black with a one-pixel lit edge on its inner and outer
// borders (R1: by distance in pixels), brighter on the top and left (the
// light comes from the upper left).
const frameVertex = `
  uniform mat4 uProj;
  uniform mat4 uView;
  varying vec2 vXY;
  void main() { vXY = position.xy; gl_Position = uProj * uView * vec4(position.xy, ${BASE_Z.toFixed(2)} + 0.01, 1.0); }
`;
const frameFragment = `
  precision highp float;
  uniform vec2 uInner;           // half size of the opening
  uniform vec2 uOuter;           // half size of the frame
  varying vec2 vXY;
  float edgeLine(float d, float fw) {
    float dpx = abs(d) / max(fw, 1e-5);
    return 1.0 - smoothstep(0.5, 1.5, dpx);
  }
  void main() {
    vec2 a = abs(vXY);
    vec2 fw = fwidth(vXY);
    // signed distances to the opening and the outer border, along each axis
    float dIn = max(a.x - uInner.x, a.y - uInner.y);
    float dOut = max(a.x - uOuter.x, a.y - uOuter.y);
    float fwi = a.x - uInner.x > a.y - uInner.y ? fw.x : fw.y;
    float fwo = a.x - uOuter.x > a.y - uOuter.y ? fw.x : fw.y;
    // lit from the upper left: the inner edge's lower and right sides face it
    float litIn = vXY.y < -uInner.y + 0.001 || vXY.x > uInner.x - 0.001 ? 1.0 : 0.45;
    float litOut = vXY.y > uOuter.y - 0.001 || vXY.x < -uOuter.x + 0.001 ? 1.0 : 0.45;
    vec3 col = vec3(0.018);
    col += vec3(0.16) * edgeLine(dIn, fwi) * litIn + vec3(0.16) * edgeLine(dOut, fwo) * litOut;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- caches: the Pi recompiles a material on every appearance otherwise ---------------
const MATERIALS = new Map();
let MATCAP = null;

function matcapTexture() {
  if (MATCAP) return MATCAP;
  const m = makeMatcap();
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
  MATCAP = { texture: t, darkShare: m.darkShare };
  return MATCAP;
}

function materialFor(vtf) {
  const key = vtf ? 'vtf' : 'attr';
  if (MATERIALS.has(key)) return MATERIALS.get(key);
  const u = {
    uProj: { value: new THREE.Matrix4() }, uView: { value: new THREE.Matrix4() },
    uCover: { value: null }, uCoverScale: { value: new THREE.Vector2(1, 1) }, uCoverClamp: { value: new THREE.Vector2(0.002, 0.002) },
    uHasCover: { value: 0 }, uLod: { value: 2 },
    uBoard: { value: new THREE.Vector2(BOARD_W, BOARD_H) },
    uRelief: { value: 1 }, uFade: { value: 1 }, uFront: { value: 9 }, uFrontW: { value: 0.12 }, uMode: { value: 0 },
    uOutside: { value: 0 }, uPush: { value: 0 }, uRecoil: { value: 1 },
    uWaves: { value: [new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4()] },
    uTime: { value: 0 },
    uMat: { value: matcapTexture().texture }, uNormals: { value: 0 }, uGain: { value: 1 }, uSilver: { value: new THREE.Color(0.6, 0.6, 0.62) },
    uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  };
  const material = new THREE.ShaderMaterial({ uniforms: u, vertexShader: vertexShader(vtf), fragmentShader });
  const frame = new THREE.ShaderMaterial({
    uniforms: { uProj: u.uProj, uView: u.uView, uInner: { value: new THREE.Vector2() }, uOuter: { value: new THREE.Vector2() } },
    vertexShader: frameVertex, fragmentShader: frameFragment
  });
  const entry = { material, frame, u };
  MATERIALS.set(key, entry);
  return entry;
}

// One pin: head centre at the origin facing +z. aPart: 1 head, 0 stem
// front ring, -1 stem back ring (moved to the base plane in the shader).
function pinGeometry(seg, pitch, rings, stemSeg) {
  const pos = [], nrm = [], part = [], idx = [];
  const hr = HEAD_R * pitch, sr = STEM_R * pitch;
  for (let r = 0; r <= rings; r++) {
    const phi = (r / rings) * Math.PI / 2;
    const z = Math.sin(phi), rr = Math.cos(phi);
    const count = r === rings ? 1 : seg;
    for (let k = 0; k < count; k++) {
      const a = (k / seg) * Math.PI * 2;
      const nx = rr * Math.cos(a), ny = rr * Math.sin(a), nz = z;
      pos.push(nx * hr, ny * hr, nz * hr); nrm.push(nx, ny, nz); part.push(1);
    }
  }
  for (let r = 0; r < rings; r++) {
    const a0 = r * seg, b0 = (r + 1) * seg;
    for (let k = 0; k < seg; k++) {
      const k1 = (k + 1) % seg;
      if (r + 1 === rings) idx.push(a0 + k, a0 + k1, b0);
      else idx.push(a0 + k, a0 + k1, b0 + k1, a0 + k, b0 + k1, b0 + k);
    }
  }
  const s0 = pos.length / 3;
  for (let ring = 0; ring < 2; ring++) {
    for (let k = 0; k < stemSeg; k++) {
      const a = (k / stemSeg) * Math.PI * 2;
      const nx = Math.cos(a), ny = Math.sin(a);
      pos.push(nx * sr, ny * sr, 0); nrm.push(nx, ny, 0); part.push(ring === 0 ? 0 : -1);
    }
  }
  for (let k = 0; k < stemSeg; k++) {
    const k1 = (k + 1) % stemSeg;
    idx.push(s0 + k, s0 + stemSeg + k, s0 + k1, s0 + k1, s0 + stemSeg + k, s0 + stemSeg + k1);
  }
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('aPart', new THREE.Float32BufferAttribute(part, 1));
  g.setIndex(idx);
  return g;
}

// The frame as a ring: outer rectangle minus the opening.
function frameGeometry(ix, iy, ox, oy) {
  const p = [-ox, -oy, ox, -oy, ox, oy, -ox, oy, -ix, -iy, ix, -iy, ix, iy, -ix, iy];
  const pos = [];
  for (let i = 0; i < 8; i++) pos.push(p[i * 2], p[i * 2 + 1], 0);
  const idx = [0, 1, 5, 0, 5, 4, 1, 2, 6, 1, 6, 5, 2, 3, 7, 2, 7, 6, 3, 0, 4, 3, 4, 7];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  return g;
}

export class PinscreenVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const rf = parseFloat(urlParam('pinrelief'));
    this._forcedRelief = Number.isFinite(rf) ? rf : null;
    this._debugWave = urlParam('pinwave') === '1';
    this._debugNormals = urlParam('pinnormals') === '1';
    this._debugDrop = urlParam('pindrop') === '1';
    this._debugPhrase = urlParam('pinphrase') === '1';
    const yw = parseFloat(urlParam('pinyaw'));
    this._forcedYaw = Number.isFinite(yw) ? yw * Math.PI / 180 : null;
  }

  // the bloom stays a rim: the two boxes are the only things over its threshold
  get postProcessing() { return { bloomStrength: 0.18, bloomThreshold: 0.78 }; }   // 0.35: thousands of boxes fogged the black (R3)

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout;
    this._vtf = urlParam('pinvtf') !== '0';
    const ent = materialFor(this._vtf);
    this._u = ent.u;
    this._material = ent.material;
    const pitch = BOARD_W / L.nx;
    this._pitch = pitch;
    const geo = pinGeometry(L.seg, pitch, L.rings, L.stemSeg);
    const n = L.nx * L.ny;
    const grid = new Float32Array(n * 2);
    this._coverAttr = new Float32Array(n * 4).fill(0.5);
    let o = 0;
    for (let j = 0; j < L.ny; j++) for (let i = 0; i < L.nx; i++) {
      grid[o++] = (i + 0.5) / L.nx; grid[o++] = (j + 0.5) / L.ny;
    }
    geo.setAttribute('iGrid', new THREE.InstancedBufferAttribute(grid, 2));
    this._iCover = new THREE.InstancedBufferAttribute(this._coverAttr, 4);
    geo.setAttribute('iCover', this._iCover);
    geo.instanceCount = n;
    this._pins = new THREE.Mesh(geo, this._material);
    this._pins.frustumCulled = false;
    // the base plane, black, and the frame around it
    this._baseMat = new THREE.ShaderMaterial({
      uniforms: { uProj: this._u.uProj, uView: this._u.uView },
      vertexShader: `uniform mat4 uProj; uniform mat4 uView; void main() { gl_Position = uProj * uView * vec4(position.xy, ${BASE_Z.toFixed(2)}, 1.0); }`,
      fragmentShader: `precision mediump float; void main() { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); }`
    });
    this._base = new THREE.Mesh(new THREE.PlaneGeometry(BOARD_W, BOARD_H), this._baseMat);
    this._base.frustumCulled = false;
    const ix = BOARD_W / 2, iy = BOARD_H / 2, fwid = FRAME_PITCHES * pitch;
    this._frameHalf = [ix + fwid, iy + fwid];
    ent.frame.uniforms.uInner.value.set(ix, iy);
    ent.frame.uniforms.uOuter.value.set(ix + fwid, iy + fwid);
    this._frame = new THREE.Mesh(frameGeometry(ix, iy, ix + fwid, iy + fwid), ent.frame);
    this._frame.frustumCulled = false;
    this._inner = new THREE.Scene();
    this._inner.add(this._base);
    this._inner.add(this._frame);
    this._inner.add(this._pins);
    this._cam = new THREE.PerspectiveCamera(FOV, 16 / 9, 0.1, 400);

    // the private multisampled target and its full-frame quad (R5: MSAA)
    const ms = parseInt(urlParam('pinmsaa'), 10);
    this._msaa = Number.isFinite(ms) && ms >= 0 && ms <= 8 ? ms : 4;
    this._rt = new THREE.WebGLRenderTarget(4, 4, {
      samples: this._msaa, type: this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType,
      depthBuffer: true, stencilBuffer: false,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false
    });
    this._quadMat = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: this._rt.texture } },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }`,
      fragmentShader: `precision mediump float; uniform sampler2D tScene; varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(tScene, vUv).rgb, 1.0); }`,
      depthTest: false, depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._quadMat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -3000;
    const self = this;
    this._clear = new THREE.Color(0, 0, 0);
    this._quad.onBeforeRender = function (renderer) { self._renderBoard(renderer); };
    scene.add(this._quad);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._beatSeconds = 60 / FREE_BPM;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null; this._lastMode = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._freeBeat = 0;
    this._sinceBar = 10; this._beatPhase = 0;
    this._sb = 0;                         // the smooth beat clock: seconds / beat length
    this._waves = [];
    this._corner = 0;
    this._event = null;
    this._lastDropTime = -1e9; this._debugDropAt = 3; this._debugPhraseAt = 3;
    this._yawSide = 1; this._yawFrom = YAW_PHRASE; this._yawTo = YAW_PHRASE; this._yawAt = -99;
    this._fitAspect = 0; this._fit = null;
    this._lastArtworkUrl = undefined;
    this._artworkTexture = null;
    this._lastPaletteRef = undefined;
    const u = this._u;
    u.uCover.value = null; u.uHasCover.value = 0;
    u.uRelief.value = this._forcedRelief != null ? this._forcedRelief : 1;
    u.uFade.value = 1; u.uFront.value = 9; u.uMode.value = 0; u.uOutside.value = 0; u.uPush.value = 0; u.uRecoil.value = 1;
    u.uNormals.value = this._debugNormals ? 1 : 0;
    u.uSilver.value.setRGB(0.6, 0.6, 0.62);
    for (const w of u.uWaves.value) w.set(0, 0, 0, 0);
    this._yawNow = this._forcedYaw != null ? this._forcedYaw : YAW_PHRASE;
    this._applyView(16 / 9);
  }

  // --- the cover --------------------------------------------------------------------

  _loadArtwork(url) {
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, function (texture) {
      if (!self._scene) { texture.dispose(); return; }
      if (self._artworkTexture) self._artworkTexture.dispose();
      self._artworkTexture = texture;
      const img = texture.image;
      const w = img && img.width ? img.width : 512, h = img && img.height ? img.height : 512;
      const u = self._u;
      u.uCover.value = texture;
      u.uCoverScale.value.copy(coverScale(BOARD_W / BOARD_H, w / h));
      const across = self._layout.nx / u.uCoverScale.value.x;
      const lod = Math.max(0, Math.log2(w / across));
      u.uLod.value = lod;
      // half a texel of that mip: the edge rows never sample past the cover
      const mw = Math.max(1, w / Math.pow(2, Math.floor(lod))), mh = Math.max(1, h / Math.pow(2, Math.floor(lod)));
      u.uCoverClamp.value.set(0.5 / mw, 0.5 / mh);
      if (!self._vtf) self._fillCoverAttribute(img);
      u.uHasCover.value = 1;
    }, function () { self._u.uHasCover.value = 0; });
  }

  _fillCoverAttribute(img) {
    if (typeof document === 'undefined' || !img) return;
    const L = this._layout, cs = this._u.uCoverScale.value;
    const cv = document.createElement('canvas');
    cv.width = L.nx; cv.height = L.ny;
    const ctx = cv.getContext('2d');
    const sw = img.width * cs.x, sh = img.height * cs.y;
    ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, 0, 0, L.nx, L.ny);
    const d = ctx.getImageData(0, 0, L.nx, L.ny).data;
    const lin = (v) => { const c = v / 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    for (let j = 0; j < L.ny; j++) for (let i = 0; i < L.nx; i++) {
      const src = ((L.ny - 1 - j) * L.nx + i) * 4, o = (j * L.nx + i) * 4;
      this._coverAttr[o] = lin(d[src]); this._coverAttr[o + 1] = lin(d[src + 1]); this._coverAttr[o + 2] = lin(d[src + 2]);
      this._coverAttr[o + 3] = 1;
    }
    this._iCover.needsUpdate = true;
  }

  _applyPalette(palette) {
    // the flat (phrase) and no-cover silver: a hint of the cover's colour (R4: vdjColor)
    const v = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors[0] : null;
    if (!v) { this._u.uSilver.value.setRGB(0.6, 0.6, 0.62); return; }
    const c = vdjColor(v, new THREE.Color(), 0.85);
    this._u.uSilver.value.setRGB(0.6, 0.6, 0.62).lerp(c.multiplyScalar(0.6 / Math.max(c.r, c.g, c.b, 1e-3)), 0.15);
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  // --- the framing (R6), once per aspect ------------------------------------------

  // A camera at yaw `yaw` and distance d, looking at the board's centre.
  _place(yaw, d) {
    const cam = this._cam;
    const t = _v.set(0, 0, H_FIT * 0.25);
    cam.position.set(t.x + Math.sin(yaw) * Math.cos(PITCH) * d, t.y + Math.sin(PITCH) * d, t.z + Math.cos(yaw) * Math.cos(PITCH) * d);
    cam.up.set(0, 1, 0);
    cam.lookAt(0, 0, H_FIT * 0.25);
    cam.updateMatrixWorld(true);
  }

  // NDC extents of a set of points for (yaw, d), no lens shift.
  _extent(pts, yaw, d) {
    this._place(yaw, d);
    _m.multiplyMatrices(this._cam.projectionMatrix, this._cam.matrixWorldInverse);
    let x0 = 9, x1 = -9, y0 = 9, y1 = -9;
    for (const p of pts) {
      const v = new THREE.Vector3(p[0], p[1], p[2]).applyMatrix4(_m);
      x0 = Math.min(x0, v.x); x1 = Math.max(x1, v.x); y0 = Math.min(y0, v.y); y1 = Math.max(y1, v.y);
    }
    return { x0, x1, y0, y1 };
  }

  // The distance that puts the pins at WIDTH_FRAC of the width at yaw 0,
  // then pushed back only as far as needed for the PINS (at full relief) to
  // keep the 5 % margin at every yaw of the range, and the frame to stay
  // inside the screen. A 16:9 board with a 2-pitch frame at 85 % of a 16:9
  // screen is taller than the screen minus two 5 % margins (1.85 vs 1.8 in
  // NDC): the frame may sit in the margin, the pins never do; its corners
  // then pass under the top UI zones (nothing important there, R6). A
  // vertical lens shift centres the pins, a little low when there is room.
  _fitFor(aspect) {
    const cam = this._cam;
    cam.aspect = aspect; cam.fov = FOV; cam.updateProjectionMatrix();
    const hw = BOARD_W / 2, hh = BOARD_H / 2, hr = HEAD_R * this._pitch;
    const flat = [], pins = [], frame = [];
    for (let k = 0; k <= 8; k++) {
      const t = k / 8;
      flat.push([-hw, -hh + 2 * hh * t, 0], [hw, -hh + 2 * hh * t, 0]);
      for (const z of [0, RELIEF_H + hr]) pins.push([-hw + 2 * hw * t, -hh, z], [-hw + 2 * hw * t, hh, z], [-hw, -hh + 2 * hh * t, z], [hw, -hh + 2 * hh * t, z]);
    }
    const fx = this._frameHalf[0], fy = this._frameHalf[1];
    for (let k = 0; k <= 8; k++) { const t = k / 8; frame.push([-fx + 2 * fx * t, -fy, BASE_Z], [-fx + 2 * fx * t, fy, BASE_Z], [-fx, -fy + 2 * fy * t, BASE_Z], [fx, -fy + 2 * fy * t, BASE_Z]); }
    let lo = 5, hi = 300;
    for (let i = 0; i < 30; i++) { const mid = (lo + hi) / 2; const e = this._extent(flat, 0, mid); if (e.x1 - e.x0 > 2 * WIDTH_FRAC) lo = mid; else hi = mid; }
    let d = hi;
    const mx = 2 * MARGIN / aspect, my = 2 * MARGIN;
    const yaws = [-YAW_MAX, -YAW_MAX / 2, 0, YAW_MAX / 2, YAW_MAX];
    const span = (pts) => { let x0 = 9, x1 = -9, y0 = 9, y1 = -9; for (const yw of yaws) { const e = this._extent(pts, yw, d); x0 = Math.min(x0, e.x0); x1 = Math.max(x1, e.x1); y0 = Math.min(y0, e.y0); y1 = Math.max(y1, e.y1); } return { x0, x1, y0, y1 }; };
    let sy = 0, P = null, F = null;
    for (let tries = 0; tries < 80; tries++) {
      P = span(pins); F = span(frame);
      const room = (1 - my) - (P.y1 - P.y0) / 2;
      sy = -(P.y0 + P.y1) / 2 - Math.min(Math.max(room, 0), 2 * PREFER_DOWN);
      const ok = Math.max(-P.x0, P.x1) <= 1 - mx && room >= 0 &&
        Math.max(-F.x0, F.x1) <= 0.99 && F.y1 + sy <= 0.99 && F.y0 + sy >= -0.99;
      if (ok) break;
      d *= 1.01;
    }
    this._fixed = { d, sy };
    this._fitAspect = aspect;
    const e0 = this._extent(flat, 0, d);
    this._fit = {
      dist: +d.toFixed(2), pinsWidth: +((e0.x1 - e0.x0) / 2).toFixed(3),
      pins: { x: [+P.x0.toFixed(3), +P.x1.toFixed(3)], y: [+(P.y0 + sy).toFixed(3), +(P.y1 + sy).toFixed(3)] },
      frame: { x: [+F.x0.toFixed(3), +F.x1.toFixed(3)], y: [+(F.y0 + sy).toFixed(3), +(F.y1 + sy).toFixed(3)] }, shift: +sy.toFixed(3)
    };
  }

  _applyView(aspect) {
    if (Math.abs(aspect - this._fitAspect) > 1e-3) this._fitFor(aspect);
    this._place(this._yawNow, this._fixed.d);
    _m.makeTranslation(0, this._fixed.sy, 0);
    this._u.uProj.value.multiplyMatrices(_m, this._cam.projectionMatrix);
    this._u.uView.value.copy(this._cam.matrixWorldInverse);
  }

  // --- the frame -----------------------------------------------------------------------

  update(deltaTime, profile) {
    const director = profile.director || {};
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
      else { this._u.uHasCover.value = 0; this._u.uCover.value = null; }
    }
    // Pause: everything holds where it is (R11: frozen, never black).
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 50, 200) : FREE_BPM;
    this._beatSeconds = 60 / bpm;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    this._sb += dt / beat;              // smooth: never re-anchored, never backwards

    // --- edges from the grid's phase (or our own clock without BPM/grid) ---
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase - 0.5) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
      this._beatPhase = profile.beatPhase;
    } else {
      this._freeBeat += dt / beat;
      if (this._freeBeat >= 1) { this._freeBeat -= 1; beatEdge = true; }
      this._beatPhase = this._freeBeat;
    }
    this._sinceBar += dt;
    if (beatEdge) this._beatCount++;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) barEdge = true;
      this._lastBarPhase = profile.barPhase;
    } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
    if (barEdge) { this._barCount++; this._sinceBar = 0; }
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (phrase == null && barEdge && this._barCount % 4 === 0) phraseEdge = true;
    if (phrase != null) this._lastPhrase = phrase;

    // --- drop / phrase ---
    let dropNow = false;
    const ev = director.nextEvent;
    if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0) dropNow = true;
    this._lastMode = director.mode;
    if (this._debugDrop && this._time >= this._debugDropAt) { dropNow = true; this._debugDropAt = this._time + 16 * beat; }
    if (this._debugPhrase && this._time >= this._debugPhraseAt) { phraseEdge = true; this._debugPhraseAt = this._time + 16 * beat; }
    if (dropNow && this._time - this._lastDropTime > 8 * beat) {
      this._lastDropTime = this._time;
      this._event = { kind: 'drop', at: this._sb };
    } else if (phraseEdge && !(this._event && this._sb - this._event.at < 9)) {
      // (an edge inside a running reform or drop is ignored: two edges a few
      // beats apart turned the view and turned it straight back)
      this._phraseCount++;
      this._event = { kind: 'phrase', at: this._sb };
      this._yawFrom = this._yawBase != null ? this._yawBase : YAW_PHRASE;
      this._yawSide = -this._yawSide;
      this._yawTo = this._yawSide * YAW_PHRASE;
      this._yawAt = this._sb;
    }

    // --- the beat: a soft wave from a corner, in turn ---
    if (beatEdge) {
      const c = [[-1, -1], [1, 1], [1, -1], [-1, 1]][this._corner++ % 4];
      const amp = this._debugWave ? WAVE_AMP * 1.3 : WAVE_AMP;
      this._waves.push({ x: c[0] * BOARD_W / 2, y: c[1] * BOARD_H / 2, born: this._sb, amp });
      if (this._waves.length > 4) this._waves.shift();
    }
    this._writeUniforms();
  }

  _writeUniforms() {
    const u = this._u, b = this._sb;
    u.uTime.value = this._time;
    const diag = Math.hypot(BOARD_W, BOARD_H);
    for (let i = 0; i < 4; i++) {
      const w = this._waves[i], out = u.uWaves.value[i];
      if (!w) { out.set(0, 0, 0, 0); continue; }
      const age = b - w.born;
      const life = clamp01(age / WAVE_LIFE_BEATS);
      out.set(w.x, w.y, age / WAVE_SPEED_BEATS * diag, w.amp * (1 - life) * smooth01(age / 0.15));
    }
    // the bar: a 10 % recoil and back within the first beat, timed in seconds
    const sinceBarBeats = this._sinceBar / this._beatSeconds;
    u.uRecoil.value = 1 - BAR_RECOIL * (sinceBarBeats < 1 ? Math.sin(Math.PI * sinceBarBeats) : 0);
    let fade = 1, front = 9, mode = 0, outside = 0, push = 0;
    const e = this._event;
    if (e) {
      const t = b - e.at;
      if (e.kind === 'phrase') {
        if (t < 1) fade = 1 - smooth01(t);
        else if (t < 9) front = (t - 1) / 8 * 1.15;
        else this._event = null;
      } else {
        if (t < 1) push = smooth01(t / 0.25);
        else if (t < 9) { mode = 1; outside = PUSH; front = (t - 1) / 8 * 1.15; }
        else this._event = null;
      }
    }
    if (this._debugWave) fade = 0;
    u.uFade.value = fade; u.uFront.value = front; u.uMode.value = mode; u.uOutside.value = outside; u.uPush.value = push;
    // the view: the phrase's side eased over TURN_BEATS, a slow sway on top, within +-12 deg
    const k = smooth01((b - this._yawAt) / TURN_BEATS);
    this._yawBase = this._yawFrom + (this._yawTo - this._yawFrom) * k;
    const yaw = this._yawBase + YAW_SWAY * Math.sin(2 * Math.PI * b / SWAY_BEATS);
    this._yawNow = this._forcedYaw != null ? this._forcedYaw : Math.max(-YAW_MAX, Math.min(YAW_MAX, yaw));
  }

  _renderBoard(renderer) {
    renderer.getDrawingBufferSize(_size);
    if (this._rt.width !== _size.x || this._rt.height !== _size.y) this._rt.setSize(_size.x, _size.y);
    this._applyView(_size.x / _size.y);
    const prevTarget = renderer.getRenderTarget();
    const prevXr = renderer.xr.enabled;
    const prevClear = renderer.getClearColor(new THREE.Color());
    const prevAlpha = renderer.getClearAlpha();
    renderer.xr.enabled = false;
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(this._clear, 1);           // R3: pure black
    renderer.clear(true, true, false);
    renderer.render(this._inner, this._cam);
    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(prevClear, prevAlpha);
    renderer.xr.enabled = prevXr;
  }

  get probe() {
    const u = this._u;
    return {
      t: this._time, beats: this._sb, bar: this._barCount, phrase: this._phraseCount,
      event: this._event ? this._event.kind : null, eventT: this._event ? this._sb - this._event.at : null,
      fade: u.uFade.value, front: u.uFront.value, mode: u.uMode.value, push: u.uPush.value, recoil: u.uRecoil.value,
      waves: this._waves.length, yaw: this._yawNow, fit: this._fit, vtf: this._vtf, hasCover: u.uHasCover.value,
      lod: u.uLod.value, pins: this._layout.nx * this._layout.ny, msaa: this._msaa,
      matcapDark: matcapTexture().darkShare
    };
  }

  dispose() {
    this._quad.onBeforeRender = null;
    this._scene.remove(this._quad);
    this._quad.geometry.dispose();
    this._quadMat.dispose();
    this._pins.geometry.dispose();
    this._base.geometry.dispose();
    this._baseMat.dispose();
    this._frame.geometry.dispose();
    this._rt.dispose();
    if (this._artworkTexture) { this._artworkTexture.dispose(); this._artworkTexture = null; }
    this._u.uCover.value = null;
    this._scene = null;
  }
}
